import { useCallback, useEffect, useMemo, useReducer } from 'react'

import type {
  DayObject,
  DayProperty,
  ObjectPropertyDefinition,
} from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import { GET_USER_ASSISTANTS } from 'src/components/Assistant/queries'
import { logger } from 'src/lib/logger'

import useObject from '../useObject'
import useObjects from '../useObjects'

import navigationReducer, {
  NavigationActions,
  type NavigationUserAction,
} from './navigation'
import { sidebarReducer } from './reducer'
import { SidebarMode } from './SidebarContext'
import SidebarContext, { SidebarActions } from './SidebarContext'

const SidebarProvider = ({
  children,
  workspaceId,
  onClose,
}: {
  children: React.ReactNode
  workspaceId: string
  onClose?: () => void
}) => {
  const { propertyDefinitions } = useObjects()
  const { object } = useObject()

  const { data: userAssistantsData } = useQuery(GET_USER_ASSISTANTS, {
    variables: {
      workspaceId,
    },
    skip: !workspaceId,
  })

  const [state, dispatch] = useReducer(sidebarReducer, {
    mode: 'threads',
    object: null,
  })

  const [navigationState, navigationDispatch] = useReducer(navigationReducer, {
    history: [],
    currentIndex: 0,
  })

  const handleSetObject = useCallback(
    ({ object, mode }: { object: Partial<DayObject>; mode?: SidebarMode }) => {
      logger.dev('SET_OBJECT', {
        object,
        mode,
      })
      dispatch({
        type: SidebarActions.SET_OBJECT,
        payload: { object, mode },
      })
    },
    []
  )

  const handleSetAssistantPrompts = useCallback(
    (prompts: string[], keyObservation: string) => {
      dispatch({
        type: SidebarActions.SET_ASSISTANT_PROMPTS,
        payload: { prompts, keyObservation },
      })
    },
    []
  )

  useEffect(() => {
    if (object && !state.object) {
      handleSetObject({ object, mode: SidebarMode.THREADS })
    }
  }, [object, state.object, handleSetObject])

  const handleUpdateProperty = useCallback((updatedProperty: DayProperty) => {
    logger.dev('SidebarProvider - handleUpdateProperty', { updatedProperty })
    dispatch({
      type: SidebarActions.UPDATE_PROPERTY,
      payload: updatedProperty,
    })
  }, [])

  const handleSetMode = useCallback((mode: SidebarMode) => {
    dispatch({
      type: SidebarActions.SET_MODE,
      payload: mode,
    })
  }, [])

  const handleNavigateForward: NavigationUserAction = useMemo(() => {
    return {
      run: () => {
        navigationDispatch({
          type: NavigationActions.NAVIGATE_FORWARD,
        })
      },
      enabled:
        navigationState.currentIndex < navigationState.history.length - 1,
    }
  }, [navigationState.history.length, navigationState.currentIndex])

  const handleNavigateBack: NavigationUserAction = useMemo(() => {
    return {
      run: () => {
        navigationDispatch({
          type: NavigationActions.NAVIGATE_BACK,
        })
      },
      enabled: navigationState.currentIndex > 0,
    }
  }, [navigationState.currentIndex])

  const handleCloseSidebar = useCallback(() => {
    dispatch({
      type: SidebarActions.RESET,
      payload: null,
    })
    onClose?.()
  }, [onClose])

  const handleExamineProperty = useCallback((propertyId: string) => {
    dispatch({
      type: SidebarActions.EXAMINE_PROPERTY,
      payload: propertyId,
    })
  }, [])

  const value = useMemo(() => {
    return {
      forward: handleNavigateForward,
      back: handleNavigateBack,
      object: state.object,
      mode: state.mode,
      workspaceId,
      setSidebarObject: handleSetObject,
      setMode: handleSetMode,
      closeSidebar: handleCloseSidebar,
      updateProperty: handleUpdateProperty,
      examineProperty: handleExamineProperty,
      propertyToExamine: state.propertyToExamine,
      propertyDefinitions: Object.values(
        propertyDefinitions?.[state.object?.objectType] ?? {}
      ) as ObjectPropertyDefinition[],
      userAssistants: userAssistantsData?.userAssistants,
      selectedAssistant: (userAssistantsData?.userAssistants ?? [])?.find(
        (assistant) => (assistant.defaultEventIds ?? []).includes('CHAT')
      ),
      setAssistantPrompts: handleSetAssistantPrompts,
      prompts: state.prompts,
      assistantObservation: state.assistantObservation,
    }
  }, [
    handleNavigateForward,
    handleNavigateBack,
    workspaceId,
    handleSetObject,
    handleSetMode,
    state.mode,
    state.object,
    handleCloseSidebar,
    handleUpdateProperty,
    handleExamineProperty,
    state.propertyToExamine,
    propertyDefinitions,
    userAssistantsData,
    handleSetAssistantPrompts,
    state.prompts,
    state.assistantObservation,
  ])

  return (
    <SidebarContext.Provider value={value}>
      {children}
      {/* {state?.object && <ObjectSidebar />} */}
    </SidebarContext.Provider>
  )
}

export default SidebarProvider
